import React, { useState } from 'react';
import './AdminLogin.scss';
import { API_BASE_URL } from '../config/api';
import { DEMO_MODE, demoApi, DEMO_ADMIN } from '../config/demo';

const AdminLogin: React.FC = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [isResetPassword, setIsResetPassword] = useState(false);
  
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [forgotForm, setForgotForm] = useState({ email: '' });
  const [resetForm, setResetForm] = useState({ token: '', newPassword: '', confirmPassword: '' });
  
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Use demo mode if enabled
      if (DEMO_MODE) {
        const result = await demoApi.login(loginForm);
        
        // Store token in localStorage
        localStorage.setItem('adminToken', result.token);
        localStorage.setItem('adminUser', JSON.stringify(result.user));

        // Redirect to admin dashboard
        window.location.href = '/admin/blogs';
        return;
      }

      // Normal backend API call
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginForm)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }

      // Store token in localStorage
      localStorage.setItem('adminToken', data.token);
      localStorage.setItem('adminUser', JSON.stringify(data.admin));

      // Redirect to admin dashboard
      window.location.href = '/admin/blogs';
    } catch (err: any) {
      // Show more helpful error message in demo mode
      if (DEMO_MODE) {
        setError(`Demo Mode: Use username "${DEMO_ADMIN.username}" and password "${DEMO_ADMIN.password}"`);
      } else {
        setError(err.message || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(forgotForm)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Request failed');
      }

      setSuccess(data.message);
      
      // For development - show the reset token
      if (data.resetToken) {
        setResetForm({ ...resetForm, token: data.resetToken });
        setTimeout(() => {
          setIsForgotPassword(false);
          setIsResetPassword(true);
        }, 2000);
      }
    } catch (err: any) {
      setError(err.message || 'Request failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (resetForm.newPassword !== resetForm.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (resetForm.newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: resetForm.token,
          newPassword: resetForm.newPassword
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Reset failed');
      }

      setSuccess('Password reset successful! Redirecting to login...');
      setTimeout(() => {
        setIsResetPassword(false);
        setIsLogin(true);
        setResetForm({ token: '', newPassword: '', confirmPassword: '' });
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Reset failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-login">
      <div className="admin-login__container">
        <div className="admin-login__header">
          <div className="admin-login__logo">🛡️</div>
          <h1 className="admin-login__title">
            {isLogin && 'Admin Login'}
            {isForgotPassword && 'Forgot Password'}
            {isResetPassword && 'Reset Password'}
          </h1>
          <p className="admin-login__subtitle">
            {isLogin && DEMO_MODE && `Demo Mode: Use username "${DEMO_ADMIN.username}" and password "${DEMO_ADMIN.password}"`}
            {isLogin && !DEMO_MODE && 'Enter your credentials to access the admin panel'}
            {isForgotPassword && 'Enter your email to receive reset instructions'}
            {isResetPassword && 'Enter your new password'}
          </p>
        </div>

        {DEMO_MODE && (
          <div className="admin-login__demo-notice">
            🎯 <strong>Demo Mode Active</strong><br />
            Username: <code>{DEMO_ADMIN.username}</code><br />
            Password: <code>{DEMO_ADMIN.password}</code>
          </div>
        )}

        {error && <div className="admin-login__error">❌ {error}</div>}
        {success && <div className="admin-login__success">✅ {success}</div>}

        {/* Login Form */}
        {isLogin && (
          <form className="admin-login__form" onSubmit={handleLogin}>
            <div className="admin-login__form-group">
              <label htmlFor="username">Username or Email</label>
              <input
                id="username"
                type="text"
                value={loginForm.username}
                onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
                placeholder="Enter your username or email"
                required
                disabled={loading}
              />
            </div>

            <div className="admin-login__form-group">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                value={loginForm.password}
                onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                placeholder="Enter your password"
                required
                disabled={loading}
              />
            </div>

            <button 
              type="submit" 
              className="admin-login__submit-btn"
              disabled={loading}
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>

            <button
              type="button"
              className="admin-login__link-btn"
              onClick={() => {
                setIsLogin(false);
                setIsForgotPassword(true);
                setError('');
              }}
            >
              Forgot Password?
            </button>
          </form>
        )}

        {/* Forgot Password Form */}
        {isForgotPassword && (
          <form className="admin-login__form" onSubmit={handleForgotPassword}>
            <div className="admin-login__form-group">
              <label htmlFor="email">Email Address</label>
              <input
                id="email"
                type="email"
                value={forgotForm.email}
                onChange={(e) => setForgotForm({ email: e.target.value })}
                placeholder="Enter your email address"
                required
                disabled={loading}
              />
            </div>

            <button 
              type="submit" 
              className="admin-login__submit-btn"
              disabled={loading}
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </button>

            <button
              type="button"
              className="admin-login__link-btn"
              onClick={() => {
                setIsForgotPassword(false);
                setIsLogin(true);
                setError('');
                setSuccess('');
              }}
            >
              Back to Login
            </button>
          </form>
        )}

        {/* Reset Password Form */}
        {isResetPassword && (
          <form className="admin-login__form" onSubmit={handleResetPassword}>
            <div className="admin-login__form-group">
              <label htmlFor="token">Reset Token</label>
              <input
                id="token"
                type="text"
                value={resetForm.token}
                onChange={(e) => setResetForm({ ...resetForm, token: e.target.value })}
                placeholder="Enter reset token"
                required
                disabled={loading}
              />
            </div>

            <div className="admin-login__form-group">
              <label htmlFor="newPassword">New Password</label>
              <input
                id="newPassword"
                type="password"
                value={resetForm.newPassword}
                onChange={(e) => setResetForm({ ...resetForm, newPassword: e.target.value })}
                placeholder="Enter new password (min 6 characters)"
                required
                disabled={loading}
              />
            </div>

            <div className="admin-login__form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <input
                id="confirmPassword"
                type="password"
                value={resetForm.confirmPassword}
                onChange={(e) => setResetForm({ ...resetForm, confirmPassword: e.target.value })}
                placeholder="Confirm new password"
                required
                disabled={loading}
              />
            </div>

            <button 
              type="submit" 
              className="admin-login__submit-btn"
              disabled={loading}
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>

            <button
              type="button"
              className="admin-login__link-btn"
              onClick={() => {
                setIsResetPassword(false);
                setIsLogin(true);
                setError('');
                setSuccess('');
              }}
            >
              Back to Login
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default AdminLogin;

